import { useCallback, useEffect, useRef, useState } from 'react';
import { useHub } from '@/contexts/HubContext';
import { getAgentosUrl } from '@/lib/agentos-url';
import { getAccessToken } from '@/lib/auth';
import type { ChatMessage } from '../types/agno-session';
import type { AgentType } from './useAgents';

interface KnowledgeReference {
  content: string;
  name: string;
  meta_data?: Record<string, unknown>;
}

interface ProposedAction {
  action_type: string;
  payload: Record<string, unknown>;
  description: string;
  reasoning: string;
}

interface RunMetrics {
  input_tokens?: number;
  output_tokens?: number;
  total_tokens?: number;
  reasoning_tokens?: number;
  duration?: number;
}

interface InspectorData {
  intentsClassified: string[];
  knowledgeReferences: KnowledgeReference[];
  proposedAction: ProposedAction | null;
  metrics: RunMetrics | null;
  modelInfo: {
    model?: string;
    modelProvider?: string;
  };
  runId?: string;
  sessionId?: string;
}

interface StreamingState {
  active: boolean;
  content: string;
  toolCalls: ChatMessage['toolCalls'];
}

/** Hub context for Support page - operator_id and client_id from HubContext */
export interface HubContextInfo {
  format: 'hub';
  operatorId?: string | null;
  /** Operator display name (e.g. "Intrum Portugal") for project field population */
  operatorName?: string | null;
  /** Operator country name (e.g. "Portugal") used to label the GitHub issue */
  operatorCountry?: string | null;
  clientId?: string | null;
  /** Client display name (e.g. "Acme Corp") so the agent knows which client is referred to */
  clientName?: string | null;
  /** Display name of the current Hub user */
  userName?: string | null;
  /** Email of the current Hub user */
  userEmail?: string | null;
  /** Pre-loaded ticket context injected before the user's message (hidden from chat UI) */
  ticketContext?: string | null;
}

/** Second param: customer info (string) or Hub context (object) */
export type SendMessageContext = string | HubContextInfo;

/**
 * Hook to manage agent/team chat interactions
 *
 * Uses SSE streaming to capture messages and tool calls in real-time from AgentOS.
 * No database polling or Supabase - just pure SSE event handling.
 * Supports both individual agents and teams.
 */
export function useAgentChat(agentId: string, agentType: AgentType = 'agent') {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [streaming, setStreaming] = useState<StreamingState | null>(null);
  const [inspectorData, setInspectorData] = useState<InspectorData>({
    intentsClassified: [],
    knowledgeReferences: [],
    proposedAction: null,
    metrics: null,
    modelInfo: {},
  });
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string>(`session-${agentType}-${agentId}-${Date.now()}`);
  const lastMessageRef = useRef<string>('');

  const { selectedOperator, selectedClient } = useHub();

  const sendMessage = useCallback(
    async (userMessage: string, context?: SendMessageContext) => {
      if (!userMessage.trim() || streaming?.active) return;

      // Clear any previous errors
      setError(null);
      lastMessageRef.current = userMessage;

      // Format message with context if provided
      const formattedMessage = (() => {
        if (typeof context === 'object' && context?.format === 'hub') {
          const operatorId = context.operatorId ?? 'none';
          const operatorName = context.operatorName ?? 'unknown';
          const clientId = context.clientId ?? 'none';
          const clientName = context.clientName ?? 'none';
          const userName = context.userName ?? 'unknown';
          const userEmail = context.userEmail ?? 'unknown';
          const operatorCountry = context.operatorCountry ?? 'none';
          const ticketPart = context.ticketContext ? `\n\n${context.ticketContext}` : '';
          return `=== HUB CONTEXT ===\noperator_id: ${operatorId}\noperator_name: ${operatorName}\noperator_country: ${operatorCountry}\nclient_id: ${clientId}\nclient_name: ${clientName}\nuser_name: ${userName}\nuser_email: ${userEmail}${ticketPart}\n\n=== USER MESSAGE ===\n${userMessage}`;
        }
        if (typeof context === 'string') {
          return `=== CUSTOMER ACCOUNT ===\n${context}\n\n=== CUSTOMER MESSAGE ===\n${userMessage}`;
        }
        return userMessage;
      })();

      // Add user message to chat immediately
      const userMsg: ChatMessage = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: userMessage,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);

      setStreaming({ active: true, content: '', toolCalls: [] });

      // Reset inspector data for new message
      setInspectorData({
        intentsClassified: [],
        knowledgeReferences: [],
        proposedAction: null,
        metrics: null,
        modelInfo: {},
      });

      // Create new AbortController for this request
      abortControllerRef.current = new AbortController();

      try {
        // Use form data as AgentOS expects application/x-www-form-urlencoded
        const formData = new URLSearchParams();
        formData.append('message', formattedMessage);
        formData.append('stream', 'true');
        formData.append('session_id', sessionIdRef.current);

        // Team runs (e.g. support-team) scope knowledge retrieval by operator and
        // reject the run with a 400 if operator_id is absent from session_state.
        formData.append(
          'session_state',
          JSON.stringify({
            ...(selectedOperator?.id && { operator_id: selectedOperator.id }),
            ...(selectedClient?.id && { client_id: selectedClient.id }),
          })
        );

        // Build endpoint based on agent type
        const endpoint = agentType === 'team' ? `${getAgentosUrl()}/teams/${agentId}/runs` : `${getAgentosUrl()}/agents/${agentId}/runs`;

        // Get auth token for AgentOS requests
        const accessToken = await getAccessToken();
        const headers: HeadersInit = {
          'Content-Type': 'application/x-www-form-urlencoded',
        };
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }

        const response = await fetch(endpoint, {
          method: 'POST',
          headers,
          body: formData,
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let currentEvent = '';
        let accumulatedContent = '';
        const toolCalls: ChatMessage['toolCalls'] = [];

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');

          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) {
              // Empty line means end of event
              currentEvent = '';
              continue;
            }

            if (trimmed.startsWith('event: ')) {
              currentEvent = trimmed.substring(7);
            } else if (trimmed.startsWith('data: ')) {
              try {
                const jsonStr = trimmed.substring(6);
                const event = JSON.parse(jsonStr);

                // Handle different event types
                if (currentEvent === 'TeamRunStarted' || currentEvent === 'RunStarted') {
                  // Capture model info and metadata
                  setInspectorData((prev) => ({
                    ...prev,
                    modelInfo: {
                      model: event.model,
                      modelProvider: event.model_provider,
                    },
                    runId: event.run_id,
                    sessionId: event.session_id,
                  }));
                } else if ((currentEvent === 'RunContent' || currentEvent === 'TeamRunContent') && event.content) {
                  // For teams: content.message_to_customer, for agents: content (string)
                  const contentText = typeof event.content === 'string' ? event.content : event.content.message_to_customer || '';
                  accumulatedContent += contentText;
                  setStreaming((prev) => (prev ? { ...prev, content: accumulatedContent } : null));

                  // Capture inspector data from content
                  if (typeof event.content === 'object') {
                    setInspectorData((prev) => ({
                      ...prev,
                      intentsClassified: event.content.intents_classified || prev.intentsClassified,
                      proposedAction: event.content.proposed_action || prev.proposedAction,
                    }));
                  }

                  // Capture knowledge references
                  if (event.references && Array.isArray(event.references)) {
                    const allReferences = event.references.flatMap((ref: { references?: KnowledgeReference[] }) => ref.references || []);
                    setInspectorData((prev) => ({
                      ...prev,
                      knowledgeReferences: allReferences,
                    }));
                  }
                } else if (currentEvent === 'TeamRunCompleted' || currentEvent === 'RunCompleted') {
                  // Capture final metrics
                  if (event.metrics) {
                    setInspectorData((prev) => ({
                      ...prev,
                      metrics: event.metrics,
                    }));
                  }
                } else if (currentEvent === 'ToolCall' || currentEvent === 'ToolCallStarted' || currentEvent === 'ToolCallCompleted') {
                  // Tools emit a start event (no result yet) and a completed event (with result).
                  // Merge them by id so a running tool is visible during execution, then updated
                  // with its result — this drives the live "Creating ticket…" status.
                  const toolId = event.id || event.tool_call_id || `tool-${Date.now()}`;
                  const existingIdx = toolCalls.findIndex((t) => t.id === toolId);
                  const prevCall = existingIdx >= 0 ? toolCalls[existingIdx] : undefined;
                  const toolCall = {
                    id: toolId,
                    tool_name: event.name || event.tool_name || prevCall?.tool_name || 'unknown',
                    tool_args: event.arguments || event.args || prevCall?.tool_args || {},
                    result: event.result ?? prevCall?.result,
                  };
                  if (existingIdx >= 0) toolCalls[existingIdx] = toolCall;
                  else toolCalls.push(toolCall);
                  setStreaming((prev) => (prev ? { ...prev, toolCalls: [...toolCalls] } : null));
                }
              } catch (error) {
                console.error('Error parsing SSE event:', error, trimmed);
              }
            }
          }
        }

        // Add assistant message with accumulated content and tool calls
        if (accumulatedContent || toolCalls.length > 0) {
          const assistantMsg: ChatMessage = {
            id: `msg-${Date.now()}-assistant`,
            role: 'assistant',
            content: accumulatedContent,
            timestamp: new Date(),
            toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
          };
          setMessages((prev) => [...prev, assistantMsg]);
        }
      } catch (error) {
        // Don't show error if it's an abort error (user cancelled)
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error sending message:', error);
          setError(error.message || 'Failed to send message. Please try again.');
        }
      } finally {
        setStreaming(null);
        abortControllerRef.current = null;
      }
    },
    [agentId, agentType, streaming?.active, selectedOperator?.id, selectedClient?.id]
  );

  const retryLastMessage = useCallback(() => {
    if (lastMessageRef.current) {
      sendMessage(lastMessageRef.current);
    }
  }, [sendMessage]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {
    messages,
    streaming,
    inspectorData,
    error,
    sendMessage,
    retryLastMessage,
    clearError: () => setError(null),
  };
}

// Export types for use in components
export type { InspectorData, KnowledgeReference, ProposedAction, RunMetrics };
